import React, { useState, useEffect } from 'react';
import { FaSave } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Alert, Button, Modal, Form, Spinner, InputGroup } from 'react-bootstrap';
import { ROLE_OPTIONS } from 'Auth/permissions';

/**
 * Is this address already taken, ignoring case? Email is case-insensitive identity, so
 * Foo@x.com and foo@x.com are the same account (C2D-1201). The API refuses the duplicate and
 * the database deliberately stays indifferent, so this is the layer that tells the admin
 * before they submit rather than after.
 */
export const findExistingEmail = (email, users) => {
  const typed = (email || '').trim().toLowerCase();
  if (!typed) return null;
  return (users || []).find((u) => ((u && u.email) || '').trim().toLowerCase() === typed) || null;
};

const AddUserModal = (props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('USER');
  const [showPassword, setShowPassword] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  // When the deployment can send email, the default is "send invite email" (no password —
  // the user sets their own via the emailed link). This flips back to today's
  // temp-password form. Irrelevant (always temp-password) when emailEnabled is false.
  const [useTempPassword, setUseTempPassword] = useState(false);

  const inviteMode = !!props.emailEnabled && !useTempPassword;

  // Checked on every keystroke so the admin is told while typing, not on a failed save.
  const duplicateOf = findExistingEmail(email, props.users);

  useEffect(() => {
    if (props.showAddUserModal) {
      setUseTempPassword(false);
      setPassword('');
      setEmail('');
    }
  }, [props.showAddUserModal]);

  return (
    <Modal
      onHide={props.handleClose}
      show={props.showAddUserModal}
    >
      <Modal.Header closeButton>
        Add user
      </Modal.Header>
      <Modal.Body>
        { props.error &&
          <Alert dismissable variant='danger'>
            { props.error }
          </Alert>
        }
        <Form.Group>
          <Form.Label>
            Email
          </Form.Label>
          <Form.Control
            type='email'
            data-testid='add-user-email'
            value={email}
            isInvalid={!!duplicateOf}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='Enter a valid email'
          />
          {duplicateOf && (
            <Form.Control.Feedback type='invalid' data-testid='add-user-email-duplicate'>
              {duplicateOf.email === email.trim()
                ? 'A user with this email already exists.'
                : `A user with this email already exists as "${duplicateOf.email}". Email addresses are not case-sensitive.`}
            </Form.Control.Feedback>
          )}
          {/* <Form.Text>User will receive an email with their temporary credentials to log-in.</Form.Text> */}
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>
            Role
          </Form.Label>
          <Form.Control
            as="select"
            value={role}
            data-testid='add-user-role'
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Control>
          <Form.Text className='text-muted'>
            {(ROLE_OPTIONS.find((o) => o.value === role) || {}).description}
          </Form.Text>
        </Form.Group>
        { inviteMode ? (
          <Form.Group className="mt-3">
            <Form.Text data-testid="add-user-invite-note">
              The user will receive an email with a link to set their own password.
            </Form.Text>
            <Button
              variant='link'
              className='p-0'
              data-testid="add-user-use-temp-password"
              onClick={() => setUseTempPassword(true)}
            >
              Set temporary password instead
            </Button>
          </Form.Group>
        ) : (
          <Form.Group className="mt-3">
            <Form.Label>
              Password
            </Form.Label>

            <InputGroup>
              <Form.Control
                data-testid="add-user-password"
                type={showPassword ? 'text' : 'password'}
                onChange={(e) => setPassword(e.target.value)}
                placeholder='Enter password'
              />
              <Button
                variant="outline-secondary"
                data-testid="add-user-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                className="d-flex align-items-center justify-content-center"
                style={{ maxWidth: '42px', borderStyle: 'none' }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </Button>
            </InputGroup>
            { props.emailEnabled && (
              <Button
                variant='link'
                className='p-0'
                data-testid="add-user-use-invite"
                onClick={() => { setUseTempPassword(false); setPassword(''); }}
              >
                Send invite email instead
              </Button>
            )}
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-end'>
        <div>
          <Button
            onClick={() => props.handleClose()}
            variant='outline-secondary'
            className='w-100'
          >
            CANCEL
          </Button>
        </div>
        <div>
          <Button
            disabled={fetchingData || !!duplicateOf}
            data-testid='add-user-save'
            onClick={async () => {
              setFetchingData(true);
              console.log('ADD USER BUTTON', email, inviteMode ? '(invite)' : password, role)
              await props.handleAddUser(email, inviteMode ? undefined : password, role);
              setFetchingData(false);
            }}
            variant='primary'
            className='w-100'
          >
            { fetchingData ? (
              <Spinner
                className='mr-2'
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
              />
            ) : (
              <FaSave className='mr-2' />
            )}
            SAVE
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AddUserModal;
